/**
 * @fileoverview Session Template Form Component
 * @created 10-10-2025 19:13:45
 * 
 * Reusable form for creating and editing session templates (global templates, no room assignment).
 * Used in Building Management flow and Room creation quick-create.
 */

import React, { useState } from 'react';
import {
  Modal,
  TextInput,
  Textarea,
  NumberInput,
  Switch,
  Button,
  Group,
  Stack,
  Card,
  Text,
  ThemeIcon
} from '@mantine/core';
import { IconClock, IconFileText } from '@tabler/icons-react';
import type { SessionTemplate } from '../../../api/endpoints/sessionTemplates';

export interface SessionTemplateFormProps {
  template?: SessionTemplate | null;
  onSubmit: (data: any) => Promise<void>;
  onClose: () => void;
  isLoading?: boolean;
  compact?: boolean; // For quick-create mode
}

/**
 * Session Template Form Component
 * Handles creation and editing of global session templates
 */
export function SessionTemplateForm({
  template,
  onSubmit,
  onClose,
  isLoading = false,
  compact = false
}: SessionTemplateFormProps): JSX.Element {
  const [formData, setFormData] = useState({
    name: template?.name || '',
    description: template?.description || '',
    startTime: template?.startTime || '',
    endTime: template?.endTime || '',
    bufferMinutes: template?.bufferMinutes || 0,
    isActive: template?.isActive ?? true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  /**
   * Validate form data
   */
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required';
    }

    if (!formData.startTime) {
      newErrors.startTime = 'Start time is required';
    }

    if (!formData.endTime) {
      newErrors.endTime = 'End time is required';
    }

    // Validate time range
    if (formData.startTime && formData.endTime) {
      const start = formData.startTime.split(':').map(Number);
      const end = formData.endTime.split(':').map(Number);
      const startMinutes = start[0] * 60 + start[1];
      const endMinutes = end[0] * 60 + end[1];

      if (startMinutes >= endMinutes) {
        newErrors.endTime = 'End time must be after start time';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    await onSubmit(formData);
  };

  /**
   * Handle field changes
   */
  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const content = (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        {/* Template Details */}
        <Card withBorder p="md">
          <Group mb="md">
            <ThemeIcon color="blue" variant="light">
              <IconFileText size={16} />
            </ThemeIcon>
            <Text fw={500}>Template Details</Text>
          </Group>
          
          <Stack gap="sm">
            <TextInput
              label="Template Name"
              placeholder="e.g., Morning Clinic, Afternoon Surgery"
              value={formData.name}
              onChange={(e) => handleFieldChange('name', e.currentTarget.value)}
              error={errors.name}
              required
              leftSection={<IconFileText size={16} />}
            />

            <Textarea
              label="Description"
              placeholder="Optional description for this template"
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.currentTarget.value)}
              minRows={2}
            />
          </Stack>
        </Card>

        {/* Time Configuration */}
        <Card withBorder p="md">
          <Group mb="md">
            <ThemeIcon color="green" variant="light">
              <IconClock size={16} />
            </ThemeIcon>
            <Text fw={500}>Time Configuration</Text>
          </Group>
          
          <Stack gap="sm">
            <Group grow>
              <TextInput
                type="time"
                label="Start Time"
                value={formData.startTime}
                onChange={(e) => handleFieldChange('startTime', e.currentTarget.value)}
                error={errors.startTime}
                required
                leftSection={<IconClock size={16} />}
              />

              <TextInput
                type="time"
                label="End Time"
                value={formData.endTime}
                onChange={(e) => handleFieldChange('endTime', e.currentTarget.value)}
                error={errors.endTime}
                required
                leftSection={<IconClock size={16} />}
              />
            </Group>

            <NumberInput
              label="Buffer Minutes"
              description="Extra time between sessions for preparation"
              value={formData.bufferMinutes}
              onChange={(value) => handleFieldChange('bufferMinutes', value || 0)}
              min={0}
              max={480}
            />

            <Switch
              label="Active Status"
              description="Inactive templates won't be available for room assignment"
              checked={formData.isActive}
              onChange={(event) => handleFieldChange('isActive', event.currentTarget.checked)}
            />
          </Stack>
        </Card>

        {/* Form Actions */}
        <Group justify="flex-end" gap="sm">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" loading={isLoading}>
            {template ? 'Update Template' : 'Create Template'}
          </Button>
        </Group>
      </Stack>
    </form>
  );

  if (compact) {
    // Return just the form content for inline embedding
    return content;
  }

  // Return modal wrapper for standalone use
  return (
    <Modal
      opened
      onClose={onClose}
      title={template ? 'Edit Session Template' : 'Create Session Template'}
      size="lg"
    >
      {content}
    </Modal>
  );
}

